// Invoicing across change orders (Planning/OverageDecision.md).
//
// A change order is a full copy of the quote it supersedes (same line ids, the
// carried lines marked `locked`). Invoicing it as-is would bill every original
// line a second time once the original was invoiced. These pure functions
// decide what a given quote's invoice should actually contain, and how several
// invoices on one gig roll up into "is it paid".
import type { Quote, QuoteLineItem } from './types.js';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

type PlanQuote = Pick<Quote, 'lineItems' | 'parentQuoteId' | 'changeOrderNumber'>;
type Ancestor = Pick<Quote, 'id' | 'lineItems' | 'invoiceNinjaInvoiceId'>;

export type InvoicePlan =
  | {
      ok: true;
      // The lines this invoice should carry.
      lineItems: QuoteLineItem[];
      // True when earlier invoices already cover part of this quote and this
      // one bills only what's new (a supplemental/change-order invoice).
      supplemental: boolean;
      changeOrderNumber?: number;
    }
  | { ok: false; reason: string };

const sameBilling = (a: QuoteLineItem, b: QuoteLineItem) =>
  round2(a.lineTotal) === round2(b.lineTotal) && a.quantity === b.quantity && round2(a.unitCost) === round2(b.unitCost);

// `ancestors` is the quote's parent chain, nearest first (parent, grandparent,
// ...). Lines already billed are those on any ancestor that has an invoice;
// line ids survive change-order copies, so they identify "the same line".
export function planInvoice(quote: PlanQuote, ancestors: Ancestor[]): InvoicePlan {
  const invoiced = new Map<string, QuoteLineItem>(); // nearest invoiced version wins
  for (const a of ancestors) {
    if (!a.invoiceNinjaInvoiceId) continue;
    for (const line of a.lineItems) if (!invoiced.has(line.id)) invoiced.set(line.id, line);
  }

  // Nothing upstream was invoiced: a normal full invoice, exactly as before.
  if (invoiced.size === 0) return { ok: true, lineItems: quote.lineItems, supplemental: false };

  const current = new Map(quote.lineItems.map(l => [l.id, l]));
  for (const [id, was] of invoiced) {
    const now = current.get(id);
    if (!now) {
      return {
        ok: false,
        reason: `"${was.description}" was invoiced earlier but is removed from this change order. Credits aren't supported yet — adjust it in Invoice Ninja directly, then restore the line here.`,
      };
    }
    if (!sameBilling(was, now)) {
      return {
        ok: false,
        reason: `"${now.description}" was invoiced at $${round2(was.lineTotal).toFixed(2)} but now reads $${round2(now.lineTotal).toFixed(2)}. Changes to an already-invoiced line can't be invoiced as a difference — put the change on a new line and restore this one.`,
      };
    }
  }

  const fresh = quote.lineItems.filter(l => !invoiced.has(l.id));
  if (fresh.length === 0) return { ok: false, reason: 'Nothing new to invoice — every line on this quote is already on an earlier invoice.' };
  return { ok: true, lineItems: fresh, supplemental: true, changeOrderNumber: quote.changeOrderNumber };
}

export interface InvoiceBalance {
  balance: number;
  paidToDate: number;
}

// A gig with several invoices (original + change orders) is paid only when
// EVERY invoice is paid off.
export function aggregateInvoiceStatus(invoices: InvoiceBalance[]): InvoiceBalance & { fullyPaid: boolean } {
  return {
    balance: round2(invoices.reduce((t, i) => t + i.balance, 0)),
    paidToDate: round2(invoices.reduce((t, i) => t + i.paidToDate, 0)),
    fullyPaid: invoices.length > 0 && invoices.every(i => i.paidToDate > 0 && i.balance <= 0),
  };
}

// Things worth a second look before issuing the FINAL invoice — shown as a
// confirm, never a block (a deposit invoice before the gig is done is
// legitimate, so nothing is raised until the gig is completed). Slice 4
// replaced "invoice from actuals" with this: the quote plus an overage change
// order already is the invoice, so the risk is invoicing while labor overage
// is undecided or payroll isn't settled.
export function preInvoiceConcerns(args: {
  gigState: string;
  overage: { status: 'NONE' | 'UNDECIDED' | 'DECIDED' | 'STALE'; overageCost: number };
  hasApprovedTime: boolean;
  unreviewedEntries: number;
  payrollState?: 'PROCESSED' | 'PAID' | null;
}): string[] {
  if (args.gigState !== 'COMPLETED') return [];
  const out: string[] = [];
  if (args.overage.status === 'UNDECIDED') {
    out.push(`Labor ran $${args.overage.overageCost.toFixed(2)} over the gig's budget and no bill-or-absorb decision has been made.`);
  } else if (args.overage.status === 'STALE') {
    out.push('The labor overage decision is out of date — the numbers changed after it was made.');
  }
  if (args.unreviewedEntries > 0) {
    out.push(`${args.unreviewedEntries} timesheet ${args.unreviewedEntries === 1 ? 'entry is' : 'entries are'} still unreviewed.`);
  }
  if (args.hasApprovedTime && args.payrollState !== 'PROCESSED' && args.payrollState !== 'PAID') {
    out.push("Payroll hasn't been finalized yet.");
  }
  return out;
}
